import { cn } from "../../lib/cn";

/**
 * Skeleton primitives. Pulse-animated placeholders matching the dark
 * dashboard palette. Use the building blocks to compose per-section
 * loaders that mirror the eventual rendered shape — so the layout
 * doesn't jump when real data lands.
 */
export function Skeleton({ className }: { className?: string }) {
	return (
		<div
			className={cn("animate-pulse rounded bg-slate-800/60", className)}
			aria-hidden="true"
		/>
	);
}

/** KPI card placeholder — header label, big value, sub-hint. */
export function SkeletonKpi() {
	return (
		<div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
			<Skeleton className="h-3 w-20" />
			<Skeleton className="mt-2 h-7 w-28" />
			<Skeleton className="mt-1.5 h-3 w-32" />
		</div>
	);
}

/** Grid of N KPI placeholders. */
export function SkeletonKpiRow({
	count = 4,
	className = "grid grid-cols-2 gap-3 md:grid-cols-4",
}: {
	count?: number;
	className?: string;
}) {
	return (
		<div className={className}>
			{Array.from({ length: count }, (_, i) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: static placeholders, never reorder
				<SkeletonKpi key={i} />
			))}
		</div>
	);
}

/** Generic table row placeholder; one bar per visual column. */
export function SkeletonRow({
	cols = 4,
	widths,
}: {
	cols?: number;
	/** Optional per-column tailwind width classes (e.g. ["w-16","w-full"]). */
	widths?: string[];
}) {
	return (
		<tr>
			{Array.from({ length: cols }, (_, i) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: static placeholders, never reorder
				<td key={i} className="px-3 py-2">
					<Skeleton className={cn("h-3", widths?.[i] ?? "w-full")} />
				</td>
			))}
		</tr>
	);
}

/** Bordered table with N skeleton rows + an optional skeleton header. */
export function SkeletonTable({
	rows = 5,
	cols = 4,
	withHeader = true,
	rowWidths,
}: {
	rows?: number;
	cols?: number;
	withHeader?: boolean;
	/**
	 * Per-column tailwind width classes for the body cells (e.g.
	 * ["w-2/3","w-1/3","w-1/4"]). When omitted every cell is `w-full`.
	 * Set this when the real table has visibly uneven columns — keeps
	 * the layout from jumping when data lands.
	 */
	rowWidths?: string[];
}) {
	return (
		<div className="overflow-hidden rounded-lg border border-slate-800">
			<table className="w-full text-sm">
				{withHeader && (
					<thead className="bg-slate-900/60">
						<tr>
							{Array.from({ length: cols }, (_, i) => (
								// biome-ignore lint/suspicious/noArrayIndexKey: static placeholders, never reorder
								<th key={i} className="px-3 py-2 text-left">
									<Skeleton className="h-2.5 w-16" />
								</th>
							))}
						</tr>
					</thead>
				)}
				<tbody className="divide-y divide-slate-800">
					{Array.from({ length: rows }, (_, i) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: static placeholders, never reorder
						<SkeletonRow key={i} cols={cols} widths={rowWidths} />
					))}
				</tbody>
			</table>
		</div>
	);
}
